import { Allocation, EnergyCompany } from "@/api/SolarCloudApi";
import { z } from "zod";
import AllocationForm from "./AllocationForm";
import { allocationFormSchema } from "./AddAllocationForm";

type UpdateAllocationFormProps = {
  allocation: Allocation;
  unallocatedWatts: number;
  energyCompanies: EnergyCompany[];
  account: "gas" | "electricity" | "bank" | undefined;
  onUpdate: (allocation: Allocation) => void;
  onCancel: () => void;
};

const updateAllocationSchema = allocationFormSchema.and(
  z.object({
    uuid: z.string().uuid(),
  })
);

type UpdateAllocationForm = z.infer<typeof updateAllocationSchema>;

export default function UpdateAllocationForm({
  unallocatedWatts,
  allocation,
  energyCompanies,
  account,
  onUpdate,
  onCancel,
}: UpdateAllocationFormProps) {
  let defaultValues: UpdateAllocationForm;

  const handleUpdate = (updatedAllocation: Allocation) => {
    onUpdate({
      ...allocation,
      ...updatedAllocation,
    });
  };

  if (allocation.bankPayee) {
    defaultValues = {
      uuid: allocation.uuid!,
      nickname: allocation.nickname!,
      wattCapacity: allocation.wattCapacity!,
      paymentType: "bank",
      accountName: allocation.bankPayee.accountName!,
      bsb: allocation.bankPayee.bsb!,
      accountNumber: allocation.bankPayee.accountNumber!,
    };
  } else if (allocation.payPalPayee) {
    defaultValues = {
      uuid: allocation.uuid!,
      nickname: allocation.nickname!,
      wattCapacity: allocation.wattCapacity!,
      paymentType: "paypal",
      paypalEmail: allocation.payPalPayee.email!,
    };
  } else if (allocation.energyCompanyPayee) {
    defaultValues = {
      uuid: allocation.uuid!,
      nickname: allocation.nickname!,
      wattCapacity: allocation.wattCapacity!,
      paymentType: "energyCompany",
      energyCompany: {
        name: allocation.energyCompanyPayee.energyCompany?.name!,
        uuid: allocation.energyCompanyPayee.energyCompany?.uuid!,
      },
      customerReferenceNumber:
        allocation.energyCompanyPayee.customerReferenceNumber!,
      billerCode: allocation.energyCompanyPayee.billerCode!,
      otherName: allocation.energyCompanyPayee.otherName || "",
    };
  } else {
    defaultValues = {
      uuid: allocation.uuid!,
      nickname: allocation.nickname!,
      wattCapacity: allocation.wattCapacity!,
      paymentType: "none",
    };
  }

  return (
    <AllocationForm
      energyCompanies={energyCompanies}
      unallocatedWatts={unallocatedWatts}
      onSubmit={handleUpdate}
      onCancel={onCancel}
      defaultValues={defaultValues}
      isUpdate={true}
      account={account}
    />
  );
}
